import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Spinner,
} from "reactstrap";
import { useTranslation } from "context/TranslationProvider";

const EMPTY_FORM = {
  title: "",
  code: "",
  description: "",
  discountType: "percentage",
  discountValue: "",
  validFrom: "",
  validUntil: "",
  maxUses: "",
  isActive: true,
};

const PromoCodeModal = ({ isOpen, toggle, onSave, editItem }) => {
  const { t } = useTranslation();
  const isEdit = Boolean(editItem);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (editItem) {
      setForm({
        title: editItem.title || "",
        code: editItem.code || "",
        description: editItem.description || "",
        discountType: editItem.discountType || "percentage",
        discountValue: editItem.discountValue ?? "",
        validFrom: editItem.validFrom ? editItem.validFrom.slice(0, 10) : "",
        validUntil: editItem.validUntil ? editItem.validUntil.slice(0, 10) : "",
        maxUses: editItem.maxUses ?? "",
        isActive: editItem.isActive ?? true,
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [editItem, isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    const payload = {
      ...form,
      discountValue: form.discountValue !== "" ? Number(form.discountValue) : undefined,
      maxUses: form.maxUses !== "" ? Number(form.maxUses) : undefined,
      validFrom: form.validFrom || undefined,
      validUntil: form.validUntil || undefined,
    };
    await onSave(payload, editItem?._id);
    setSaving(false);
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalHeader toggle={toggle} className="border">
        {isEdit
          ? t("promoCodes.editTitle", "Edit Promo Code")
          : t("promoCodes.createTitle", "Create Promo Code")}
      </ModalHeader>

      <ModalBody>
        <div className="row">
          <div className="col-md-6">
            <FormGroup>
              <Label className="form-control-label">
                {t("promoCodes.fields.title", "Title")}
              </Label>
              <Input
                name="title"
                value={form.title}
                onChange={handleChange}
                placeholder={t("promoCodes.fields.titlePlaceholder", "e.g. Summer Sale")}
              />
            </FormGroup>
          </div>
          <div className="col-md-6">
            <FormGroup>
              <Label className="form-control-label">
                {t("promoCodes.fields.code", "Code")}
              </Label>
              <Input
                name="code"
                value={form.code}
                onChange={handleChange}
                placeholder="e.g. SAVE20"
                disabled={isEdit}
                style={{ textTransform: "uppercase" }}
              />
            </FormGroup>
          </div>
        </div>

        <FormGroup>
          <Label className="form-control-label">
            {t("promoCodes.fields.description", "Description")}
            <span className="text-muted font-weight-normal ml-1">
              ({t("common.optional", "optional")})
            </span>
          </Label>
          <Input
            name="description"
            value={form.description}
            onChange={handleChange}
            placeholder={t("promoCodes.fields.descriptionPlaceholder", "Short description shown to users")}
          />
        </FormGroup>

        <div className="row">
          <div className="col-md-6">
            <FormGroup>
              <Label className="form-control-label">
                {t("promoCodes.fields.discountType", "Discount Type")}
              </Label>
              <Input type="select" name="discountType" value={form.discountType} onChange={handleChange}>
                <option value="percentage">{t("promoCodes.discountType.percentage", "Percentage (%)")}</option>
                <option value="fixed">{t("promoCodes.discountType.fixed", "Fixed (€)")}</option>
              </Input>
            </FormGroup>
          </div>
          <div className="col-md-6">
            <FormGroup>
              <Label className="form-control-label">
                {t("promoCodes.fields.discountValue", "Discount Value")}
              </Label>
              <Input
                type="number"
                name="discountValue"
                value={form.discountValue}
                onChange={handleChange}
                min={1}
                max={form.discountType === "percentage" ? 100 : undefined}
                placeholder={form.discountType === "percentage" ? "e.g. 20" : "e.g. 5"}
              />
            </FormGroup>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <FormGroup>
              <Label className="form-control-label">
                {t("promoCodes.fields.validFrom", "Valid From")}
                <span className="text-muted font-weight-normal ml-1">({t("common.optional", "optional")})</span>
              </Label>
              <Input type="date" name="validFrom" value={form.validFrom} onChange={handleChange} />
            </FormGroup>
          </div>
          <div className="col-md-6">
            <FormGroup>
              <Label className="form-control-label">
                {t("promoCodes.fields.validUntil", "Valid Until")}
                <span className="text-muted font-weight-normal ml-1">({t("common.optional", "optional")})</span>
              </Label>
              <Input type="date" name="validUntil" value={form.validUntil} onChange={handleChange} />
            </FormGroup>
          </div>
        </div>

        <div className="row align-items-center">
          <div className="col-md-6">
            <FormGroup className="mb-0">
              <Label className="form-control-label">
                {t("promoCodes.fields.maxUses", "Max Uses")}
                <span className="text-muted font-weight-normal ml-1">
                  ({t("promoCodes.fields.maxUsesHint", "leave empty for unlimited")})
                </span>
              </Label>
              <Input
                type="number"
                name="maxUses"
                value={form.maxUses}
                onChange={handleChange}
                min={1}
                placeholder="∞"
              />
            </FormGroup>
          </div>
          <div className="col-md-6 mt-md-4">
            <FormGroup check className="mt-2 mt-md-0">
              <Label check>
                <Input
                  type="checkbox"
                  name="isActive"
                  checked={form.isActive}
                  onChange={handleChange}
                />{" "}
                {t("promoCodes.fields.isActive", "Active")}
              </Label>
            </FormGroup>
          </div>
        </div>
      </ModalBody>

      <ModalFooter className="border">
        <Button color="secondary" onClick={toggle} disabled={saving}>
          {t("common.cancel", "Cancel")}
        </Button>
        <Button color="warning" onClick={handleSubmit} disabled={saving}>
          {saving ? (
            <Spinner size="sm" />
          ) : isEdit ? (
            t("button.save", "Save")
          ) : (
            t("promoCodes.create", "Create")
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default PromoCodeModal;
